import React, { useState, useEffect } from 'react';
import { ChartAreaSkeleton } from '../common/loading';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
Typography,
  Alert,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { customerRequestApi } from '../../services/api';

interface FileViewerProps {
  open: boolean;
  onClose: () => void;
  filePath: string;
  fileName: string;
  fileType?: string;
  displayName?: string; // Optional custom display name (defaults to fileName)
}

export const FileViewer: React.FC<FileViewerProps> = ({
  open,
  onClose,
  filePath,
  fileName,
  fileType,
  displayName,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open && filePath) {
      loadFile();
    } else {
      // Cleanup: revoke object URL when closing
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
        setFileUrl(null);
      }
    }

    return () => {
      // Cleanup on unmount
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [open, filePath]);

  const loadFile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Extract file path from GCS URL if needed
      const path = extractFilePath(filePath);

      // Get file content from backend
      const { blob: fileBlob, contentType } = await customerRequestApi.viewFile(path);
      
      // Use content type from response, fallback to fileType prop or infer from extension
      let mimeType = contentType;
      if (!mimeType || mimeType === 'application/octet-stream') {
        // Infer from file extension if content type not available
        const extension = fileName.split('.').pop()?.toLowerCase() || '';
        const mimeTypes: Record<string, string> = {
          'pdf': 'application/pdf',
          'png': 'image/png',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'gif': 'image/gif',
          'webp': 'image/webp',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        };
        mimeType = mimeTypes[extension] || fileType || 'application/octet-stream';
      }
      
      // Create blob URL for viewing
      const blob = new Blob([fileBlob], { type: mimeType });
      const url = URL.createObjectURL(blob);
      setFileUrl(url);
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading file:', err);
      
      // Handle error response - could be JSON error in blob format
      if (err.response?.data instanceof Blob) {
        try {
          // Try to parse the blob as text to extract JSON error
          const text = await err.response.data.text();
          try {
            const errorData = JSON.parse(text);
            const errorMessage = errorData.detail || errorData.message || 'Failed to load file. Please try again.';
            setError(errorMessage);
            
            // If it's an authentication error, suggest re-login
            if (err.response?.status === 401) {
              setError(`${errorMessage}. Please refresh the page and try again.`);
            }
          } catch {
            // If not JSON, use the text as error message
            setError(text || 'Failed to load file. Please try again.');
          }
        } catch (parseError) {
          setError('Failed to load file. Please try again.');
        }
      } else if (err.response?.status === 401) {
        // Authentication error
        const errorMessage = err.response?.data?.detail || 'Invalid authentication credentials. Please refresh the page and try again.';
        setError(errorMessage);
      } else if (err.response?.status === 403) {
        // Permission error
        setError(err.response?.data?.detail || 'Access denied. You do not have permission to view this file.');
      } else if (err.response?.status === 404) {
        // File not found
        setError(err.response?.data?.detail || 'File not found or inaccessible.');
      } else {
        // Other errors
        setError(err.response?.data?.detail || err.message || 'Failed to load file. Please try again.');
      }
      setLoading(false);
    }
  };

  const extractFilePath = (url: string): string => {
    if (url.startsWith('gs://')) {
      const parts = url.replace('gs://', '').split('/');
      return parts.slice(1).join('/'); // Remove bucket name
    }
    if (url.includes('storage.googleapis.com')) {
      const match = url.match(/\/o\/(.+?)(?:\?|$)/);
      return match ? decodeURIComponent(match[1]) : url;
    }
    return url;
  };

  const handleFullscreen = () => {
    if (fileUrl) {
      const iframe = document.querySelector('iframe[data-file-viewer]') as HTMLIFrameElement;
      if (iframe) {
        if (iframe.requestFullscreen) {
          iframe.requestFullscreen();
        }
      }
    }
  };

  const handleDownload = () => {
    if (fileUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Determine if file can be viewed inline
  const canViewInline = () => {
    const type = fileType?.toLowerCase() || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    // PDFs can be viewed inline
    if (type.includes('pdf') || extension === 'pdf') return true;
    
    // Images can be viewed inline
    if (type.includes('image') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return true;
    }
    
    // Office documents might be viewable via Google Docs Viewer or similar
    // For now, we'll show PDFs and images inline, others as download
    return false;
  };

  const renderFileContent = () => {
    if (!fileUrl) return null;

    const type = fileType?.toLowerCase() || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    // PDF viewer
    if (type.includes('pdf') || extension === 'pdf') {
      const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: '600px',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
          onContextMenu={handleContextMenu}
          onDragStart={(e) => e.preventDefault()}
          onMouseDown={(e) => {
            // Prevent text selection
            if (e.detail > 1) {
              e.preventDefault();
            }
          }}
        >
          {/* Overlay to catch right-clicks */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              pointerEvents: 'none',
              userSelect: 'none',
            }}
            onContextMenu={handleContextMenu}
          />
          <embed
            data-file-viewer
            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            type="application/pdf"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              minHeight: '600px',
              pointerEvents: 'auto',
            }}
            title={fileName}
            onContextMenu={handleContextMenu}
          />
        </Box>
      );
    }

    // Image viewer
    if (type.includes('image') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            minHeight: '400px',
            backgroundColor: alpha(SEARCE_COLORS.gray_400, 0.05),
            p: 2,
          }}
          onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
        >
          <img
            src={fileUrl}
            alt={fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              pointerEvents: 'none', // Prevent drag and drop
              userSelect: 'none', // Prevent selection
            }}
            draggable={false} // Prevent dragging
          />
        </Box>
      );
    }

    // For other file types, show message that preview is not available
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: SEARCE_COLORS.textPrimary }}>
          Preview not available
        </Typography>
        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
          This file type ({extension || 'unknown'}) cannot be previewed in the viewer.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: SEARCE_COLORS.textSecondary, fontSize: '0.875rem' }}>
          File: {fileName}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${SEARCE_COLORS.border}`,
          pb: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
          {displayName || fileName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {fileUrl && (
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: SEARCE_COLORS.textSecondary,
                '&:hover': {
                  backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                  color: SEARCE_COLORS.primary,
                },
              }}
              title="Download file"
            >
              <DownloadIcon />
            </IconButton>
          )}
          {canViewInline() && fileUrl && (
            <IconButton
              onClick={handleFullscreen}
              size="small"
              sx={{
                color: SEARCE_COLORS.textSecondary,
                '&:hover': {
                  backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                  color: SEARCE_COLORS.primary,
                },
              }}
              title="Fullscreen"
            >
              <FullscreenIcon />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: SEARCE_COLORS.textSecondary,
              '&:hover': {
                backgroundColor: alpha(SEARCE_COLORS.error, 0.1),
                color: SEARCE_COLORS.error,
              },
            }}
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <ChartAreaSkeleton titleWidth="35%" height={320} />
          </Box>
        )}
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {!loading && !error && fileUrl && (
          <Box 
            sx={{ 
              flex: 1, 
              overflow: 'auto',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }}
            onDragStart={(e) => e.preventDefault()}
          >
            {renderFileContent()}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

